// Vendored from the JETCAM DXF Viewer (src/geometry/conic.ts), 2026-09-02.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Local edits are marked "dxfsplitter:". Do not reformat; keep diffable against origin.

import { type Vec3 } from "../parse/index.ts";
import { add, applyPoint, applyVector, length, scale, type Transform } from "./transform.ts";

/**
 * Circles, arcs and ellipses, all handled as one thing.
 *
 * Every one of them is a point sweeping two conjugate semi-axis vectors:
 *
 *     P(t) = centre + cos(t) * u + sin(t) * v
 *
 * A circle has perpendicular `u` and `v` of equal length; an ellipse does not.
 * The useful part is that this survives an affine transform exactly — the
 * image of the curve is the same expression with `u` and `v` replaced by their
 * own images — so transforming a conic is three vector transforms and no
 * special cases for rotation, mirroring or non-uniform scale.
 *
 * `t` runs from `start` to `end`, and `end` may be less than `start`, which is
 * how a clockwise sweep is represented.
 */
export interface Conic {
  readonly center: Vec3;
  readonly u: Vec3;
  readonly v: Vec3;
  readonly start: number;
  readonly end: number;
}

export function conicPoint(c: Conic, t: number): Vec3 {
  return add(c.center, add(scale(c.u, Math.cos(t)), scale(c.v, Math.sin(t))));
}

export function transformConic(t: Transform, c: Conic): Conic {
  return {
    center: applyPoint(t, c.center),
    u: applyVector(t, c.u),
    v: applyVector(t, c.v),
    start: c.start,
    end: c.end,
  };
}

/** A circle in the XY plane, in the form the renderer and `ctx.arc` want. */
export interface CircularArc {
  readonly center: Vec3;
  readonly radius: number;
  readonly startAngle: number;
  readonly endAngle: number;
  readonly ccw: boolean;
  readonly fullCircle: boolean;
}

/** Relative slack for the "is this still a circle" tests. */
const SHAPE_EPSILON = 1e-9;
const FULL_TURN = Math.PI * 2;

/**
 * Recover a true circular arc, or null if the conic is not one.
 *
 * Three things have to hold: the curve must lie in a plane parallel to XY (or
 * the on-screen projection is an ellipse), and `u` and `v` must still be
 * perpendicular and equal in length (or the shape itself is an ellipse). A
 * non-uniform INSERT scale breaks the last two, which is exactly when an arc
 * has to become a polyline.
 */
export function asCircularArc(c: Conic): CircularArc | null {
  const lu = length(c.u);
  const lv = length(c.v);
  const r = Math.max(lu, lv);
  if (r === 0) return null;

  const tol = SHAPE_EPSILON * r;
  if (Math.abs(c.u.z) > tol || Math.abs(c.v.z) > tol) return null; // not parallel to XY
  if (Math.abs(lu - lv) > tol) return null; // scaled unevenly
  if (Math.abs(c.u.x * c.v.x + c.u.y * c.v.y + c.u.z * c.v.z) > tol * r) return null; // sheared

  // Sign of the 2D cross product says which way increasing `t` turns. Combined
  // with the direction `t` itself travels, that gives the sweep direction. A
  // mirror flips the first; a clockwise source arc flips the second.
  const crossZ = c.u.x * c.v.y - c.u.y * c.v.x;
  const ccw = crossZ > 0 === c.end > c.start;

  const angleAt = (t: number): number => {
    const p = conicPoint(c, t);
    return Math.atan2(p.y - c.center.y, p.x - c.center.x);
  };

  const span = Math.abs(c.end - c.start);
  const fullCircle = span >= FULL_TURN - SHAPE_EPSILON;

  return {
    center: c.center,
    radius: lu,
    startAngle: fullCircle ? 0 : angleAt(c.start),
    endAngle: fullCircle ? FULL_TURN : angleAt(c.end),
    ccw,
    fullCircle,
  };
}

/** Hard cap so a pathological radius or tolerance cannot allocate forever. */
const MAX_SEGMENTS = 16384;

/**
 * Sample a conic finely enough that no chord departs from the true curve by
 * more than `tolerance`.
 *
 * The step comes from the sagitta of a circular arc, `r * (1 - cos(step/2))`,
 * using the longer semi-axis as the radius. For an ellipse that over-samples
 * the flatter ends, which costs a few points and is never wrong in the
 * direction that matters.
 */
export function tessellateConic(c: Conic, tolerance: number): Vec3[] {
  const r = Math.max(length(c.u), length(c.v));
  const span = c.end - c.start;

  let segments = 1;
  if (r > tolerance && tolerance > 0) {
    const maxStep = 2 * Math.acos(Math.min(1, Math.max(-1, 1 - tolerance / r)));
    segments = maxStep > 0 ? Math.ceil(Math.abs(span) / maxStep) : MAX_SEGMENTS;
  }
  segments = Math.min(MAX_SEGMENTS, Math.max(1, segments));

  const points: Vec3[] = [];
  for (let i = 0; i <= segments; i += 1) {
    points.push(conicPoint(c, c.start + (span * i) / segments));
  }
  return points;
}

// ---------------------------------------------------------------------------
// Constructors
// ---------------------------------------------------------------------------

/** A full circle of radius `r`, in the XY plane of its own coordinate system. */
export function circleConic(center: Vec3, r: number): Conic {
  return {
    center,
    u: { x: r, y: 0, z: 0 },
    v: { x: 0, y: r, z: 0 },
    start: 0,
    end: FULL_TURN,
  };
}

/**
 * A DXF ARC, which always sweeps counter-clockwise from start to end in its
 * own coordinate system. An end angle at or below the start means the arc
 * crosses zero, so it is lifted by a full turn rather than treated as empty.
 */
export function arcConic(center: Vec3, r: number, startAngle: number, endAngle: number): Conic {
  let end = endAngle;
  while (end <= startAngle) end += FULL_TURN;
  return {
    center,
    u: { x: r, y: 0, z: 0 },
    v: { x: 0, y: r, z: 0 },
    start: startAngle,
    end,
  };
}

/**
 * A DXF ELLIPSE. `majorAxis` is the vector from the centre to the end of the
 * major axis; the minor axis is that turned a quarter turn about the entity's
 * normal and shortened by `ratio`. Start and end are already radians in the
 * file, and are parameters of this expression rather than true angles.
 */
export function ellipseConic(
  center: Vec3,
  majorAxis: Vec3,
  ratio: number,
  startParam: number,
  endParam: number,
  normal: Vec3,
): Conic {
  // v = normal x majorAxis, scaled by the ratio: a quarter turn in the plane.
  const minor = {
    x: (normal.y * majorAxis.z - normal.z * majorAxis.y) * ratio,
    y: (normal.z * majorAxis.x - normal.x * majorAxis.z) * ratio,
    z: (normal.x * majorAxis.y - normal.y * majorAxis.x) * ratio,
  };

  let end = endParam;
  // A writer marking a full ellipse with equal start and end parameters is
  // asking for the whole thing, not for nothing.
  if (Math.abs(end - startParam) < SHAPE_EPSILON) end = startParam + FULL_TURN;
  while (end < startParam) end += FULL_TURN;

  return { center, u: majorAxis, v: minor, start: startParam, end };
}
